import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Alert, CircularProgress, Typography,
  Paper, MenuItem, TextField, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { useConsultarTransaccionesHoy, useCvus } from '../../hooks/useCvus';
import type { ConsultaTransaccionesResult } from '../../hooks/useCvus';

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Si se pasa, la consulta es directa contra esa empresa (sin selector).
   * Si NO se pasa, el dialog muestra un selector de empresas con CVU.
   */
  empresaId?: string;
  empresaLabel?: string;
}

/**
 * Dialog reutilizable para consultar a Pronto Pago las transacciones del día
 * (ventana 00:00 a 24:00 del servidor PP). Útil como respaldo del webhook
 * y para QA inmediato después de una transferencia real.
 */
export default function ConsultarPagosDialog({
  open,
  onClose,
  empresaId: empresaIdProp,
  empresaLabel: empresaLabelProp,
}: Props) {
  const { data: cvusResult } = useCvus();
  const cvus = cvusResult?.cvus ?? [];
  const consultar = useConsultarTransaccionesHoy();

  // Si no nos pasaron empresaId, el selector lo elige el usuario
  const [empresaIdLocal, setEmpresaIdLocal] = useState('');
  const empresaIdEfectivo = empresaIdProp ?? empresaIdLocal;
  const empresaSeleccionada = cvus.find((c) => c.empresa_id === empresaIdEfectivo);

  const [result, setResult] = useState<ConsultaTransaccionesResult | null>(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      consultar.reset();
      if (!empresaIdProp) setEmpresaIdLocal('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConsultar = async () => {
    if (!empresaIdEfectivo) return;
    try {
      const r = await consultar.mutateAsync(empresaIdEfectivo);
      setResult(r);
    } catch {
      // se muestra abajo
    }
  };

  const apiError = consultar.error as Error | null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <CloudDownloadIcon color="info" />
          <span>Consultar pagos de hoy en Pronto Pago</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ fontSize: 13 }}>
            Esta consulta solo devuelve transferencias del{' '}
            <strong>día actual</strong> (00:00 a 24:00 del servidor de Pronto
            Pago). Para histórico, mirá la bandeja de Transacciones — esos
            llegan automáticamente por webhook.
          </Alert>

          {apiError && <Alert severity="error">{apiError.message}</Alert>}

          {!empresaIdProp && (
            <TextField
              select
              label="CVU a consultar"
              required
              fullWidth
              value={empresaIdLocal}
              onChange={(e) => setEmpresaIdLocal(e.target.value)}
              helperText={
                cvus.length === 0
                  ? 'No hay CVUs cargadas todavía.'
                  : 'Elegí el CVU de la empresa cuyas transferencias querés consultar.'
              }
              disabled={cvus.length === 0}
            >
              <MenuItem value=""><em>Seleccioná una empresa…</em></MenuItem>
              {cvus.map((c) => (
                <MenuItem key={c.id} value={c.empresa_id}>
                  {c.empresa?.razon_social ?? '—'} — {c.alias}
                </MenuItem>
              ))}
            </TextField>
          )}

          {empresaIdProp && empresaLabelProp && (
            <Typography variant="body2" color="text.secondary">
              Consultando: <strong>{empresaLabelProp}</strong>
              {empresaSeleccionada?.alias && ` — ${empresaSeleccionada.alias}`}
            </Typography>
          )}

          {result && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" color="text.secondary">
                  Respuesta de Pronto Pago
                </Typography>
                <Typography variant="caption" color={result.mode === 'mock' ? 'warning.main' : 'success.main'}>
                  modo {result.mode}
                </Typography>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  m: 0, p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  fontSize: 11,
                  overflow: 'auto',
                  maxHeight: 280,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {JSON.stringify(result.data, null, 2)}
              </Typography>
              {result.mode === 'mock' && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>
                  Modo mock: no se llama a Pronto Pago. Para transacciones de
                  prueba, usá el simulador de webhook.
                </Alert>
              )}
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={consultar.isPending}>
          Cerrar
        </Button>
        <Button
          variant="contained"
          color="info"
          onClick={handleConsultar}
          disabled={!empresaIdEfectivo || consultar.isPending}
          startIcon={
            consultar.isPending ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />
          }
        >
          {consultar.isPending ? 'Consultando…' : 'Consultar ahora'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
